export interface ItemOption {
  id: string;
  label: string;
  order: number;
  value: number;
}
export interface FormItem {
  id: string;
  category: string;
  itemType: ItemType;
  questionType?: QuestionType;
  label: string;
  optional: boolean;
  minVariance?: number;
  options: ItemOption[];
  order: number;
  recipientType: ItemRecipientType;
  scale?: number;
  subjectType: ItemSubjectType;
  visibility?: ItemVisibility;
  pointsAllocationMode?: PointsAllocationMode;
}

export enum ItemType {
  Instructions = "IN",
  Text = "TX",
  Choice = "CH",
  Points = "PT",
  Blank = "BL",
}

export enum QuestionType {
  YesNo = "YN",
  Likert = "LK",
  Scale = "SC",
  Generic = "GN",
}

export const ItemTypeLabels = {
  IN: "Instructions",
  TX: "Text",
  CH: "Choice",
  PT: "Points",
  BL: "Blank",
};

export const QuestionTypeLabels = {
  YN: "Yes/No",
  LK: "Likert",
  SC: "Scale",
  GN: "Generic Choice",
};

export enum ItemRecipientType {
  TeamMembers = "TM",
  TeamLeaders = "TL",
  NonLeaders = "NL",
  TeamInstructors = "TI",
  /** @deprecated Use TeamMembers instead - consolidated for backward compatibility */
  TeamLearners = "LE",
}

export enum ItemSubjectType {
  PeerSelf = "PR",
  PeerOnly = "PO",
  Team = "TM",
  Course = "CS",
  General = "GL",
  LeadSelf = "LS",
  LeadOnly = "LO",
  NonLeadSelf = "NS",
  NonLeadOnly = "NO",
  TeamInstructors = "TI",
  /** @deprecated Use PeerSelf instead - consolidated for backward compatibility */
  TeamLearners = "LE",
}

export enum ItemVisibility {
  Releasable = "RE",
  Private = "PR",
}

export enum TemplateType {
  Survey = "SV",
  Rubric = "RB",
}

export interface Template {
  id: string;
  active: boolean;
  institutionCreated: boolean;
  isDirectShare: boolean;
  isGlobal: boolean;
  isShared: boolean;
  isSnapshot: boolean;
  isMine: boolean;
  isUsedInDraftOrScheduled: boolean;
  instructions: string;
  items: FormItem[];
  modified: string;
  name: string;
  user: User;
  templateType: TemplateType;
}

export interface Answer {
  id: string;
  created?: string;
  itemId: string;
  modified: string;
  text?: string;
  value?: number | null;
}

export interface ResponseSubject {
  id: string;
  answers: Answer[];
  completed: boolean;
  subjectType: ResponseSubjectType;
  subjectUserId: string | null;
  isTeamLead: boolean;
  subjectUserIsInstructor?: boolean;
  subjectUserIsLearner?: boolean;
}

export interface ResponseSubjectWithAuthor {
  id: string;
  answers: Answer[];
  completed: boolean;
  subjectType: ResponseSubjectType;
  subjectUserId: string | null;
  authorDisplayName: string;
}

export enum ResponseSubjectType {
  Peer = "PR",
  Team = "TM",
  Course = "CS",
  General = "GL",
  /** Team instructor ratings (students rating instructors); same backend mode as Peer for results. */
  TeamInstructors = "TI",
}

export interface Response {
  id: string;
  authorId: string;
  campaignId: string;
  campaignName: string;
  campaignState: CampaignState;
  campaignAutoSendDate?: string | null;
  campaignType: CampaignType;
  groupId?: string;
  isTeamLead: boolean;
  form?: Template;
  assignment?: Assignment;
  submitted: boolean;
  reopened: boolean;
  submittedDate?: Date;
  hasAnswers: boolean;
  hasReceivedFeedback: boolean;
  responseSubjects: ResponseSubject[];
  tokenizedLink: string;
  /** Per-course coaching opt-out (FL-333 round-4) — false disables all
   *  coaching UI for this response. */
  responseCoachingEnabled?: boolean;
}

export interface MyResponse extends Partial<Response> {
  id: string;
  authorId: string;
  campaignId: string;
  campaignName: string;
  campaignDueDate: Date;
  campaignState: CampaignState;
  campaignReleased: Date;
  campaignResultsLink: string;
  campaignLoopStudent: boolean;
  hasLoopResponse: boolean;
  groupId: string;
  submitted: boolean;
  tokenizedLink: string;
}

export interface Course {
  id: string;
  name: string;
  fullSyncCapable?: boolean;
  fullSyncReady?: boolean;
  fullSyncEnabled?: boolean;
  lmsType?: string;
  oauthUrl?: string;
  gradePushCapable?: boolean;
  gradePushReady?: boolean;
  includeInstructorsInTeams?: boolean;
  hideOverallAverages?: boolean;
  responseCoachingEnabled?: boolean;
}

export interface Section {
  id: string;
  name: string;
  course: Course;
  userIds: string[];
}

export enum CampaignState {
  Draft = "DR",
  AssignmentScheduled = "ACS",
  AssignmentActive = "ACA",
  AssignmentClosed = "ACC",
  Scheduled = "SC",
  Active = "AC",
  Closed = "CL",
  Archived = "AR",
}

export enum CampaignType {
  Survey = "SV",
  Assignment = "AS",
}

export enum CampaignGradingMode {
  FixedAuto = "FA",
  Manual = "MN",
}

export interface CampaignActivity {
  id: string;
  label: string;
  created: string;
}

export enum SubmissionType {
  Document = "DOC",
  Video = "VID",
  Text = "TXT",
  Link = "LNK",
  LivePresentation = "LIV",
}

export interface Assignment {
  id: string;
  description: string;
  pdfAttachment?: string | null;
  pdfAttachmentUrl?: string | null;
  submissionType: SubmissionType;
  assignmentSendDate?: string | null;
  assignmentDueDate?: string | null;
  assignmentCloseDate?: string | null;
  submissions?: AssignmentSubmission[];
}

export enum AssignmentSubmissionStatus {
  Draft = "DR",
  Submitted = "SB",
  Graded = "GR",
}

export interface AssignmentSubmission {
  id: string;
  assignment: Assignment;
  assignmentId: string;
  authorId: string;
  groupId?: string | null;
  status: AssignmentSubmissionStatus;
  submissionText: string;
  submissionFileUrl?: string | null;
  submittedDate?: string | null;
  grade?: number | null;
  instructorFeedback: string;
  tokenizedLink: string;
}

export interface Campaign {
  id: string;
  name: string;
  state: CampaignState;
  campaignType: CampaignType;
  assignment?: Assignment;
  assignmentEnabled: boolean;
  assignmentGradingMode?: CampaignGradingMode;
  assignmentPoints?: string;
  assignmentRatingProportion?: number;
  autoCloseDate: string | null;
  autoReleaseDate: string | null;
  autoSendDate: string | null;
  course: Course;
  dueDate?: string;
  formId?: string;
  form?: Template;
  groupcategory?: GroupCategory;
  groups: Team[];
  released?: string | null;
  sectionId?: string;
  activity: CampaignActivity[];
  submittedResponses?: number;
  totalResponses?: number;
  gradesSyncedToLms: string | null;
  loopInstructor: boolean;
  loopStudent: boolean;
  loopFeedbackSummary: LoopFeedbackSummary[];
  sendBlockReason?: string | null;
}

export interface CampaignSummary {
  id: string;
  name: string;
}

export interface Analysis {
  id?: string;
  question: FormItem;
  self: number | undefined;
  others: number | undefined;
  gap: number | undefined;
  given: number | undefined;
  user: User;
  team?: Team;
  label: string;
  outlier?: boolean;
  /** Relative Performance Score: individual received ÷ team received. */
  rps?: number;
  timestamp?: Date;
  othersInstructor?: number;
  othersStudent?: number;
  othersLead?: number;
}

export interface SectionAnalysis {
  section: string;
  self: number | undefined;
  others: number | undefined;
  gap: number | undefined;
  given: number | undefined;
  user: User;
  label: string;
}

export interface StudentAverageAnalysis {
  self: number | undefined;
  others: number | undefined;
  gap: number | undefined;
  given: number | undefined;
  user: User;
  label: string;
  id: string;
  outlier: boolean;
  rps?: number;
}

export interface TeamAverageAnalysis {
  self: number | undefined;
  others: number | undefined;
  gap: number | undefined;
  given: number | undefined;
  team: Team;
  label: string;
  id: string;
  outlier: boolean;
  rps?: number;
}

export interface GenericAverageAnalysis {
  self: number | undefined;
  others: number | undefined;
  gap: number | undefined;
  given: number | undefined;
  label: string;
  id: string;
  outlier: boolean;
  rps?: number;
}

export interface CommentItem {
  id?: string;
  comment: string;
  respondent: User;
  recipient: User;
  team?: Team;
  timestamp?: Date;
}

export interface AggregateComments {
  id?: string;
  question: FormItem;
  comments: CommentItem[];
}

export interface PointRow {
  id?: string;
  question: FormItem;
  value: number;
  user: User;
  sender: User;
  team?: Team;
  label: string;
  timestamp?: Date;
}

export interface User {
  id: string;
  email: string;
  headshot: string;
  firstName: string;
  lastName: string;
  ssoId: string;
  isLearner: boolean;
  isInstructor?: boolean;
}

export interface Team {
  id: string;
  leaderId?: string;
  userIds: string[];
  name: string;
  isHidden?: boolean;
  hasInstructor?: boolean;
  // Group set the team belongs to; null for manually-created teams.
  categoryId?: string | null;
  categoryName?: string | null;
  // sectionId: string;
}

export interface Data {
  responses: Response[];
  sections: Section[];
  teams: Team[];
  templates: Template[];
  users: User[];
  campaigns: Campaign[];
}

export interface OverallValues {
  self: number;
  given: number;
  received: number;
  /** Relative Performance Score for the viewing user, relative to their team. */
  rps?: number;
}

export interface Results {
  id: string;
  analyses: Analysis[];
  aggregates: AggregateComments[];
  overall: OverallValues;
  campaign: Campaign;
  form: Template;
  user: User;
  hasMixedAuthors?: boolean;
  hasMultipleRaterTypes?: boolean;
}

export interface GroupCategory {
  id: string;
  name: string;
}

export const NewCategory = "New Category";

export enum PointsAllocationMode {
  UniqueAllocation = "UA",
  MinimumVariance = "MV",
}

export interface LoopResponse {
  id: string;
  message: string;
  sender: User;
  subject: User;
  loopType: string;
}

export interface LoopFeedbackSummary {
  subjectId: string;
  hasStudentFeedback: boolean;
  hasInstructorFeedback: boolean;
}

// ── Matching ──────────────────────────────────────────────────────────────────

export enum MatchingRunStatus {
  Draft = "DR",
  SurveyActive = "SA",
  Processing = "PR",
  Ready = "RD",
  Applied = "AP",
  LmsSyncing = "SY",
  LmsSynced = "LS",
}

export enum MatchingQuestionType {
  TimeAvailability = "time_availability",
  RolePreference = "role_preference",
  Personality = "personality",
  PreferredTeammates = "preferred_teammates",
  AvoidTeammates = "avoid_teammates",
  Gender = "gender",
  CustomText = "custom_text",
}

export interface MatchingQuestion {
  id: string;
  questionType: string;
  label: string;
  description: string;
  weight: number;
  order: number;
  options: unknown[];
  required: boolean;
  /** Per-question similarity preference: -1=diverse, 0=neutral/use config, 1=similar. null = use config-level. */
  similarityPreference?: number | null;
}

export interface MatchingConfig {
  id: string;
  name: string;
  description: string;
  timePreferenceWeight: number;
  rolePreferenceWeight: number;
  personalityWeight: number;
  preferredTeammatesWeight: number;
  similarityPreference: number;
  isTemplate: boolean;
  created: string;
  modified: string;
  questions: MatchingQuestion[];
}

export interface MatchingRespondent {
  userId: string;
  name: string;
  email: string;
  responded: boolean;
  surveyLink: string;
}

export interface MatchingOutputMember {
  id: string;
  userId: string;
  name: string;
  email: string;
  order: number;
}

export interface MatchingOutputTeam {
  id: string;
  order: number;
  name: string;
  score: number | null;
  rationale: string;
  conflicts?: string[];
  members: MatchingOutputMember[];
  appliedGroupId: string | null;
}

export interface MatchingRun {
  id: string;
  configId: string;
  courseId: string;
  courseLmsType: string;
  status: MatchingRunStatus;
  teamSize: number;
  numTeams: number | null;
  surveyDueDate: string | null;
  reminderEnabled: boolean;
  reminderFrequency: string;
  notes: string;
  launchedAt: string | null;
  processedAt: string | null;
  appliedAt: string | null;
  lmsSyncedAt: string | null;
  score: number | null;
  analysis: Record<string, unknown> | null;
  errorMessage: string;
  created: string;
  modified: string;
  outputTeams: MatchingOutputTeam[];
  responseCount: number;
  tokenCount: number;
  responseRate: number;
}

export interface MatchingAnswerData {
  questionId: string;
  valueJson: unknown;
}

export interface RosterMember {
  id: string;
  name: string;
  email: string;
}

export interface MatchingSurveyData {
  runId: string;
  courseName: string;
  dueDate: string | null;
  notes: string;
  questions: MatchingQuestion[];
  existingAnswers: MatchingAnswerData[];
  alreadySubmitted: boolean;
  roster: RosterMember[];
}

export interface MatchingSuccess {
  ok: boolean;
  message: string;
}

// ── Submission Review types ────────────────────────────────────────────────

export enum SubmissionReviewStatus {
  Draft = "DR",
  Scheduled = "SD",
  SubmissionsOpen = "SO",
  SubmissionsClosed = "SC",
  ReviewsOpen = "RO",
  ReviewsClosed = "RC",
  ResultsReleased = "RL",
}

export interface SubmissionReviewActivity {
  id: string;
  name: string;
  description: string;
  status: SubmissionReviewStatus;
  submissionType: SubmissionType;
  rubricId?: string | null;
  rubric?: Template | null;
  rubricName?: string | null;
  rubricItems?: {
    id: string;
    label: string;
    itemType: string;
    order: number;
  }[];
  openDate?: string | null;
  dueDate?: string | null;
  closeDate?: string | null;
  reviewOpenDate?: string | null;
  reviewDueDate?: string | null;
  reviewCloseDate?: string | null;
  resultsReleaseDate?: string | null;
  reviewsPerSubmission: number;
  submitterMode: string;
  reviewerMode: string;
  allowSelfReview: boolean;
  anonymityMode: string;
  maxPoints: number;
  submissionWeight: number;
  reviewWeight: number;
  participationWeight: number;
  loopStudent: boolean;
  loopInstructor: boolean;
  /** FL-303: create a gradebook assignment in the LMS at open. */
  assignmentEnabled: boolean;
  /** FL-301: resource-link id of the LMS assignment, set at open. */
  assignmentRlid?: string | null;
  pdfAttachmentUrl?: string | null;
  courseId: string;
  /** FL-305: grade-push readiness surface (same shape campaigns use). */
  course?: Course;
  groupcategoryId?: string | null;
  submissions?: SubmissionReviewSubmission[];
  submissionCount?: number;
  submittedCount?: number;
  unmappedSubmitterNames?: string[];
  reviewCompletionRate?: number;
  completedReviews?: number;
  totalReviews?: number;
  activityLogs?: SubmissionReviewActivityLogEntry[];
  participantsPreview?: ParticipantTeam[];
  manualPairs?: ManualReviewPair[];
  gradesSyncedToLms?: string | null;
  created: string;
  modified: string;
}

export interface ManualReviewPair {
  id: string;
  /** student reviewer — exactly one of reviewerId/reviewerGroupId is set */
  reviewerId?: string | null;
  /** team reviewer: every member reviews the subject (FL-336) */
  reviewerGroupId?: string | null;
  reviewerName: string;
  subjectGroupId?: string | null;
  subjectUserId?: string | null;
}

export interface ParticipantPreview {
  id: string;
  name: string;
}

export interface ParticipantTeam {
  id: string;
  name: string;
  memberCount: number;
  members: ParticipantPreview[];
}

export interface SubmissionReviewSubmission {
  id: string;
  authorId: string;
  authorName: string;
  groupName?: string | null;
  status: string;
  submittedAt?: string | null;
  submissionFileUrl?: string | null;
  submissionText: string;
  grade?: number | null;
  instructorFeedback: string;
  studentReflection?: string;
  tokenizedLink: string;
  reviewCount: number;
  allocations: ReviewAllocationRecord[];
}

export interface ReviewAllocationRecord {
  id: string;
  submissionId: string;
  reviewerId: string;
  reviewerName: string;
  hasReview: boolean;
  reviewSubmitted: boolean;
  reviewLink: string;
  order: number;
  review?: SubmissionReviewRecord | null;
}

export interface SubmissionReviewRecord {
  id: string;
  submitted: boolean;
  submittedAt?: string | null;
  answers: ReviewAnswerRecord[];
  tokenizedLink: string;
}

export interface ReviewAnswerRecord {
  id: string;
  itemId: string;
  value?: number | null;
  text: string;
}

export interface SubmissionReviewActivityLogEntry {
  id: string;
  label: string;
  created: string;
}

export interface SubmissionReviewSuccess {
  ok: boolean;
  message: string;
}

export interface MyMatchingSurvey {
  runId: string;
  courseName: string;
  dueDate: string | null;
  alreadySubmitted: boolean;
  surveyLink: string;
  notes: string;
}
